<script lang="ts" setup>
	import { computed } from "vue";
	import { Menu } from "lucide-vue-next";
	import { useMobileMenu } from "@/composables/useMobileMenu";
	import { useNavItems } from "@/composables/useNavItems";

	// Phone-only primary navigation. Up to four destinations get a tab; the rest,
	// plus credits and the account links, live behind the Menu tab so nothing is
	// ever squeezed below a comfortable thumb target.
	const MAX_TABS = 4;

	const { items, activeId } = useNavItems();
	const menu = useMobileMenu();

	const tabs = computed(() => items.value.slice(0, MAX_TABS));

	// If the current page is one of the overflow destinations, the Menu tab
	// lights up so the user still sees where they are.
	const overflowActive = computed(() => {
		const active = activeId.value;
		if (!active) return false;
		return !tabs.value.some((item) => item.id === active);
	});
</script>

<template>
	<nav class="mobile-tab-bar" aria-label="Main">
		<NuxtLink
			v-for="item in tabs"
			:key="item.id"
			:to="item.to"
			class="tab"
			:class="{ active: item.id === activeId }"
			:aria-current="item.id === activeId ? 'page' : undefined"
			:aria-label="item.badgeAriaLabel"
			@click="menu.close()"
		>
			<span class="tab-icon">
				<component :is="item.icon" :size="22" aria-hidden="true" />
				<span v-if="item.badge" class="tab-dot" aria-hidden="true"></span>
			</span>
			<span class="tab-label">{{ item.label }}</span>
		</NuxtLink>

		<button type="button" class="tab" :class="{ active: overflowActive || menu.open.value }" :aria-expanded="menu.open.value" aria-controls="mobile-menu-sheet" @click="menu.toggle()">
			<span class="tab-icon">
				<Menu :size="22" aria-hidden="true" />
			</span>
			<span class="tab-label">Menu</span>
		</button>
	</nav>
</template>

<style scoped lang="scss">
	.mobile-tab-bar {
		display: none;
		flex-shrink: 0;
		z-index: 120;
		width: 100%;
		min-height: calc(var(--tabbar-height) + var(--safe-bottom));
		padding: 0 4px var(--safe-bottom);
		box-sizing: border-box;
		background: var(--bg-surface);
		border-top: 1px solid var(--border);
		user-select: none;

		@include phone {
			display: flex;
		}
	}

	.tab {
		position: relative;
		flex: 1 1 0;
		min-width: 0;
		display: flex;
		flex-direction: column;
		align-items: center;
		justify-content: center;
		gap: 3px;
		height: var(--tabbar-height);
		padding: 0 4px;
		border: 0;
		border-radius: var(--radius-md);
		background: transparent;
		color: var(--text-muted);
		text-decoration: none;
		cursor: pointer;
		-webkit-tap-highlight-color: transparent;
		transition: color var(--dur-fast) var(--ease-out);

		.tab-icon {
			position: relative;
			display: flex;
			align-items: center;
			justify-content: center;
			width: 26px;
			height: 26px;
		}

		.tab-dot {
			position: absolute;
			top: 0;
			right: 0;
			width: 8px;
			height: 8px;
			border-radius: 50%;
			background: var(--accent);
			box-shadow: 0 0 0 2px var(--bg-surface);
		}

		.tab-label {
			@include GoogleSans(false);
			max-width: 100%;
			font-size: 11px;
			font-weight: 500;
			letter-spacing: -0.1px;
			line-height: 1;
			white-space: nowrap;
			overflow: hidden;
			text-overflow: ellipsis;
		}

		&.active {
			color: var(--accent);
		}

		&:focus-visible {
			outline: 2px solid var(--focus);
			outline-offset: -2px;
		}
	}
</style>
